import { useEffect, useRef, useState } from 'react';
import type { Avatar } from '../backend/types.ts';
import { useModel } from '../state.tsx';
import { Card, Pill, Switch } from '../ui/components.tsx';
import { PrivacyPolicy } from './Legal.tsx';

// Email first, then consent: consents are stored on the account, so asking before sign-in meant
// asking twice when the email link opened a fresh page.
type Step = 'welcome' | 'signIn' | 'consent' | 'profile';
const STEPS: Step[] = ['welcome', 'signIn', 'consent', 'profile'];

export function Onboarding() {
  const model = useModel();
  // Signed in (code or email link) but not finished: continue at consent, or at profile if already given.
  const consented = model.consents.has('terms') && model.consents.has('health_data');
  const [step, setStep] = useState<Step>(!model.profile ? 'welcome' : consented ? 'profile' : 'consent');
  const [consent, setConsent] = useState({ terms: false, health: false, sharing: true });
  useEffect(() => { if (step === 'consent' && consented) setStep('profile'); }, [step, consented]);

  const saveConsent = async () => {
    await model.backend.grantConsent('terms');
    await model.backend.grantConsent('health_data');
    if (consent.sharing) await model.backend.grantConsent('trainer_sharing');
    await model.signedIn();
    setStep('profile');
  };

  return (
    <div className="screen plain" style={{ minHeight: '100dvh' }}>
      {step !== 'welcome' && (
        <div className="progress" aria-hidden="true"><div style={{ width: `${(STEPS.indexOf(step) / (STEPS.length - 1)) * 100}%` }} /></div>
      )}
      {step === 'welcome' && <Welcome next={() => setStep('signIn')} />}
      {step === 'signIn' && <SignIn next={() => setStep('consent')} />}
      {step === 'consent' && <Consent value={consent} onChange={setConsent} next={saveConsent} />}
      {step === 'profile' && <ProfileStep />}
    </div>
  );
}

function Welcome({ next }: { next: () => void }) {
  const { backend } = useModel();
  return (
    <div className="stack" style={{ flex: 1, justifyContent: 'flex-end', gap: 20, paddingBottom: 12 }}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <path d="M2 12h4l2-6 4 12 3-9 2 3h5" />
      </svg>
      <h1 style={{ fontSize: 44 }}>Recover</h1>
      <p className="caption" style={{ fontSize: 17 }}>
        Log what your body did, not the exercises. Tap the muscles you worked, rate the session, and your trainer walks in
        knowing what’s already been loaded this week.
      </p>
      {backend.isDemo && <Pill text="Demo mode: data stays in this browser" color="var(--warning)" />}
      <button className="btn primary" onClick={next} data-testid="onboarding-start">Get started</button>
    </div>
  );
}

function Consent({ value, onChange, next }: {
  value: { terms: boolean; health: boolean; sharing: boolean };
  onChange: (v: { terms: boolean; health: boolean; sharing: boolean }) => void;
  next: () => Promise<void>;
}) {
  const [policy, setPolicy] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const row = (key: keyof typeof value, title: string, detail: string, required: boolean) => (
    <Card>
      <div className="toggle-row">
        <div className="text">
          <div className="row"><h2 style={{ fontSize: 17 }}>{title}</h2>{required && <Pill text="Required" color="var(--text-2)" />}</div>
          <p className="caption">{detail}</p>
        </div>
        <Switch checked={value[key]} onChange={(v) => onChange({ ...value, [key]: v })} label={title} />
      </div>
    </Card>
  );
  if (policy) {
    return (
      <div className="stack">
        <button className="link" onClick={() => setPolicy(false)}>‹ Back</button>
        <PrivacyPolicy />
      </div>
    );
  }
  return (
    <div className="stack" style={{ gap: 16 }}>
      <h1>Your data, your call</h1>
      <p className="caption" style={{ fontSize: 16 }}>
        Recover handles health information. We collect only what the app needs, encrypt it, and you can delete everything at any time.
      </p>
      {row('terms', 'Terms and privacy policy', 'I agree to the terms and the privacy policy.', true)}
      {row('health', 'Health and training data',
        'Store the muscles I log, intensity and recovery ratings, my daily check-ins and the workouts I enter, to show my recovery.', true)}
      {row('sharing', 'Share with my trainer',
        'When I link a trainer, they get a WhatsApp digest before my sessions (what I worked, ratings, solo workouts) and can send me notes. I choose exactly what is shared and can stop at any time.', false)}
      <button className="link" onClick={() => setPolicy(true)}>Read the privacy policy</button>
      {error && <p className="caption danger" role="alert">{error}</p>}
      <button className="btn primary" disabled={!value.terms || !value.health || working} data-testid="consent-continue" onClick={async () => {
        setWorking(true); setError(null);
        try { await next(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); setWorking(false); }
      }}>{working ? 'Saving…' : 'Continue'}</button>
    </div>
  );
}

function SignIn({ next }: { next: () => void }) {
  const model = useModel();
  const { backend } = model;
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const done = useRef(false);
  const finish = async () => {
    if (done.current) return;
    done.current = true;
    await model.signedIn();
    next();
  };

  // Already signed in (email link opened on this phone), or signed in from another tab.
  useEffect(() => {
    void backend.userId().then((id) => { if (id) void run(finish); });
    return backend.onAuthChange(async () => { if (await backend.userId()) await run(finish); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn: () => Promise<void>) => {
    setWorking(true); setError(null);
    try { await fn(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setWorking(false); }
  };

  const validEmail = /^\S+@\S+\.\S+$/.test(email.trim());
  // The email carries a code and a link (custom SMTP template). The code also works in a Home Screen app,
  // which the link would open in Safari instead.
  const codeField = sent;
  return (
    <form className="stack" style={{ gap: 16 }} onSubmit={(e) => {
      e.preventDefault();
      if (!sent) void run(async () => { await backend.sendCode(email.trim()); setSent(true); });
      else if (codeField) void run(async () => { await backend.verifyCode(email.trim(), code.trim()); await finish(); });
    }}>
      <h1>{sent ? 'Check your email' : 'Sign in to Recover'}</h1>
      {!sent ? (
        <>
          <p className="caption" style={{ fontSize: 16 }}>
            Enter your email and we send you a sign-in code. No password; if you are new, the same code creates your
            account. Your trainer never sees your email.
          </p>
          <input className="field" type="email" inputMode="email" autoComplete="email" placeholder="you@example.com"
            value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email" data-testid="email" />
        </>
      ) : (
        <Card>
          <p>We sent a sign-in code to <strong>{email.trim()}</strong>.</p>
          <p className="caption" style={{ fontSize: 15 }}>
            Open “Your Recover sign-in code” and type the code below, or tap the link in the email on this phone.
          </p>
          <p className="caption">Not there after a minute? Check spam. The code works once and expires after an hour.</p>
        </Card>
      )}
      {codeField && (
        <input className="field code" inputMode="numeric" autoComplete="one-time-code" maxLength={8} placeholder="Enter the code"
          value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} aria-label="Sign-in code" data-testid="code" />
      )}
      {backend.isDemo && sent && <p className="caption warning">Demo mode: no email is sent. Use any 6 to 8 digits.</p>}
      {error && <p className="caption danger" role="alert">{error}</p>}
      {(!sent || codeField) && (
        <button className="btn primary" type="submit" disabled={working || (!sent ? !validEmail : code.length < 6)} data-testid="signin-submit">
          {working ? 'Please wait…' : sent ? 'Sign in with code' : 'Email me a sign-in code'}
        </button>
      )}
      {sent && (
        <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <button type="button" className="link" onClick={() => { setSent(false); setCode(''); }}>Use a different email</button>
        </div>
      )}
    </form>
  );
}

function ProfileStep() {
  const model = useModel();
  const [name, setName] = useState(model.profile?.full_name ?? '');
  const [avatar, setAvatar] = useState<Avatar>(model.profile?.avatar ?? 'male');
  const [working, setWorking] = useState(false);
  const valid = name.trim().length > 0;
  return (
    <form className="stack" style={{ gap: 18 }} onSubmit={async (e) => {
      e.preventDefault();
      setWorking(true);
      await model.updateProfile({ full_name: name.trim(), avatar, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      await model.finishOnboarding();
    }}>
      <h1>About you</h1>
      <div className="stack" style={{ gap: 8 }}>
        <span className="eyebrow">Name your trainer knows you by</span>
        <input className="field" autoComplete="name" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)}
          aria-label="Full name" data-testid="profile-name" />
      </div>
      <div className="stack" style={{ gap: 8 }}>
        <span className="eyebrow">Your body model</span>
        <AvatarPicker value={avatar} onChange={setAvatar} />
        <p className="caption">Both bodies use the same muscle map, so your logs and your trainer’s view are identical.</p>
      </div>
      <button className="btn primary" type="submit" disabled={!valid || working} data-testid="profile-continue">
        {working ? 'Saving…' : 'Finish'}
      </button>
    </form>
  );
}

export function AvatarPicker({ value, onChange }: { value: Avatar; onChange: (a: Avatar) => void }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {(['male', 'female'] as Avatar[]).map((a) => (
        <button key={a} type="button" aria-pressed={value === a} onClick={() => onChange(a)} className="card"
          style={{ alignItems: 'center', minHeight: 72, justifyContent: 'center', borderWidth: 2,
            borderColor: value === a ? 'var(--accent)' : 'var(--stroke)', color: value === a ? 'var(--accent)' : 'var(--text-2)' }}>
          <h2>{a === 'male' ? 'Male' : 'Female'}</h2>
        </button>
      ))}
    </div>
  );
}
